import React, { useEffect, useRef, useState } from "react";
import ModalContainer from "../../components/ModalContainer.jsx";
import { redirect, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { getTicketContextsService, updateTicket, getTicket, getAllTicketsService } from "../../services/ticketsServices.js";
import TicketContext from "../../components/TicketContext.jsx";
import { convertDateToJalaali } from "../../utils/convertDate.js";
import Alert from "../../utils/Alert.js";
import { CircularProgress, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { ErrorMessage, Form, Formik } from "formik";
import GetField from "../../components/GetField.jsx";
import {
  initialValues,
  onSubmit,
  validationSchema,
} from "./contextFormikCodes.js";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import ShowError from "../../components/ShowError.jsx";

const Context = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [contexts, setContexts] = useState([]);
  const [fileName, setFileName] = useState("");
  const [sendSms, setSendSms] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)
  const [closeTicket, setCloseTicket] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const [data, setData] = useState({
    send_sms: false,
    send_email: false,
    status: "o"
  })

  const location = useLocation();
  const navigate = useNavigate();
  const contextListRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaContainerRef = useRef(null);


  const handleGetTicketContexts = async (ticketCode) => {
    setIsLoading(true);
    try {
      const response = await getTicketContextsService(ticketCode);
      if (response.status === 200) {
        const allContexts = response.data.map((c) => ({
          ...c,
          date: convertDateToJalaali(c.sending_date, "jYYYY/jMM/jDD"),
          time: convertDateToJalaali(c.sending_date, "HH:mm"),
        }));
        setContexts(allContexts);
      }
    } catch (error) {
      Alert(
        `خطا ${error.response?.status || ""}!`,
        "در دریافت پیام‌ها مشکلی رخ داده است!\nلطفاً مجدداً تلاش کنید.",
        "error"
      );
      return navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTicket = async (ticketCode, data) => {
    setIsLoading(true);
    try {
      const response = await updateTicket(ticketCode, data);
    } catch (error) {
      Alert(
        `خطا ${error.response?.status || ""}!`,
        "در دریافت پیام‌ها مشکلی رخ داده است!\nلطفاً مجدداً تلاش کنید.",
        "error"
      );
      return navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const handlegetTicket = async (ticketCode) => {
    setIsLoading(true);
    try {
      const response = await getTicket(ticketCode);
      setSendEmail(response.data.send_email)
      setSendSms(response.data.send_sms)
      setCloseTicket(response.data.status === "c" ? true : false)

    } catch (error) {
      Alert(
        `خطا ${error.response?.status || ""}!`,
        "در دریافت پیام‌ها مشکلی رخ داده است!\nلطفاً مجدداً تلاش کنید.",
        "error"
      );
      return navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFile = (ev, formik) => {
    const file = ev.target.files[0];
    if (!file) return;
    formik.setFieldValue("file", file);
    setFileName(file.name);
  };
  
  async function handleChangeSms() {  
    const newValue = !sendSms; 
    setSendSms(newValue);  
    Alert("انجام عملیات", "تیکت با موفقیت ویرایش شد", "success");  
    
    const newData = {  
        ...data,   
        send_sms: newValue,  
        send_email: sendEmail,   
        status: closeTicket ? "c" : "a"  
    };  
    setData(newData);  

    await handleUpdateTicket(location.state.code, newData);
  }

  async function handleChangeEmail() {
    const newValue = !sendEmail
    setSendEmail(newValue);
    Alert("انجام عملیات", "تیکت با موفقیت ویرایش شد", "success");

    const newData = {  
      ...data,   
      send_sms: sendSms,  
      send_email: newValue,   
      status: closeTicket ? "c" : "a"  
    };  
    setData(newData);  

    await handleUpdateTicket(location.state.code, newData);
  }

  async function handleChangeCloseTicket() {
    const newValue = !closeTicket
    setCloseTicket(newValue)
    Alert("انجام عملیات", "تیکت با موفقیت ویرایش شد", "success");

    const newData = {  
      ...data,   
      send_sms: sendSms,  
      send_email: sendEmail,   
      status: newValue ? "c" : "a"  
    };  
    setData(newData);  

    await handleUpdateTicket(location.state.code, newData);
  }

  const handleRemoveFile = (formik) => {
    formik.setFieldValue("file", "");
    setFileName("");
  };

  useEffect(() => {
    if (location.state) {
      handleGetTicketContexts(location.state.code);
      handlegetTicket(location.state.code);
    } else {
      return navigate(-1);
    }
  }, []);

  useEffect(() => {
    if (contextListRef && contextListRef.current && contexts.length > 0) {
      contextListRef.current.scrollTop = contextListRef.current.scrollHeight;
    }
  }, [contextListRef, contexts]);

  const handleCloseModal = () => {
    setIsOpen(false);
    navigate("/tickets", { state: { refresh: true } });
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleCloseModal}
      fullscreen={true}
      title={`موضوع: ${location.state?.subject}`}
    >
      <div
        ref={contextListRef}
        className={`w-full scroll-smooth 
                absolute right-0 overflow-auto 
                px-5 pt-6 dir-ltr children-dir-rtl
                ${
                  location.state?.status === "o" || location.state?.status === "a"
                    ? "context-list-open bottom-40"
                    : "context-list-closed bottom-0"
                }`}
      >
        {isLoading ? (
          <div
            className="w-full flex flex-col 
                    items-center justify-center
                    px-3 py-10"
          >
            <CircularProgress size={40} />
            <span
              className="text-primary mt-4 
                        text-lg opacity-animation"
            >
              صبر کنید...
            </span>
          </div>
        ) : (
          contexts.map((c, index) => (
            <div
              key={`context_${index}`}
              className={`w-full mb-5 flex
                            ${
                              c.sender === "a" ? "justify-start" : "justify-end"
                            }`}
            >
              <TicketContext
                context={c.context}
                date={c.date}
                time={c.time}
                code={c.code}
                file={c.file}
                className={`context-item ${
                  c.sender === "a" ? "bg-blue-400" : "bg-gray-300"
                }`}
              />
            </div>
          ))
        )}
      </div>
      {(location.state?.status === "o" || location.state?.status === "a") && (
        <Formik
          initialValues={initialValues}
          onSubmit={(values, actions) => {
            onSubmit(
              values,
              actions,
              location.state,
              setContexts,
              setFileName,
              textareaContainerRef
            );
            }
          }
          validationSchema={validationSchema}
          validateOnMount
        >

          {(formik) => {
            return (
              <Form
                className="absolute bottom-0 
                      right-0 w-full h-40 flex flex-row 
                      items-center px-4 py-1 bg-white 
                      border-t-2 border-black overflow-hidden"
              >
                <div className="flex-1" ref={textareaContainerRef}>
                  {/* <div className="flex flex-row">
                    <div className="w-full">
                      <input
                        type="checkbox"
                        id="send_sms"
                        name="send_sms"
                        onChange={formik.handleChange}
                        checked={formik.values.send_sms}
                      />
                      <label htmlFor="send_sms">ارسال پیامک</label>
                    </div>
                    <div className="w-full">
                      <input
                        type="checkbox"
                        id="send_email"
                        name="send_email"
                        onChange={formik.handleChange}
                        checked={formik.values.send_email}
                      />
                      <label htmlFor="send_email">ارسال ایمیل</label>
                    </div>
                  </div> */}

                  <div className="flex items-center space-x-3 mb-2">  
                    <div className="flex items-center">  
                      <input  
                        id="send-email"  
                        type="checkbox"  
                        checked={sendEmail} 
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"  
                        onChange={handleChangeEmail}
                      />  
                      <label htmlFor="send-email" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">  
                        ارسال ایمیل  
                      </label>  
                    </div>  
                    
                    <div className="flex items-center p-4">  
                      <input    
                        id="send-sms"  
                        type="checkbox"  
                        checked={sendSms}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"  
                        onChange={handleChangeSms}  
                      />  
                      <label htmlFor="send-sms" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">  
                        ارسال پیامک  
                      </label>  
                    </div>  

                    <div className="flex items-center p-0">  
                      <input    
                        id="close-ticket"  
                        type="checkbox"  
                        checked={closeTicket}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"  
                        onChange={handleChangeCloseTicket}  
                      />  
                      <label htmlFor="close-ticket" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">  
                        اتمام تیکت  
                      </label>  
                    </div>  
                  </div>  
                  
                  <GetField
                    control="textarea"
                    name="context"
                    placeholder="متن خود را وارد کنید..."
                    rows={2}
                    unresizable={true}
                    unshownError={true}
                    className="relative mb-5"
                    fieldClass="pe-12 py-1.5"
                    autoFocus={true}
                  >
                    <IconButton
                      onClick={() => fileInputRef.current.click()}
                      sx={{
                        position: "absolute",
                        left: "1.3rem",
                        top: "0",
                        bottom: "0",
                        my: "auto",
                        width: "30px",
                        height: "30px",
                      }}
                    >
                      <AttachFileIcon
                        sx={{
                          fontSize: "1.3rem",
                          rotate: "45deg",
                        }}
                        color="primary"
                      />
                    </IconButton>
                  </GetField>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(ev) => handleChangeFile(ev, formik)}
                  />
                  {fileName && (
                    <>
                      <div
                        className="w-full mt-2 
                                      flex items-center 
                                      text-sm break-all"
                      >
                        <span
                          onClick={() => handleRemoveFile(formik)}
                          className="cursor-pointer"
                          title="حذف فایل"
                        >
                          <FolderOffIcon
                            sx={{
                              fontSize: "1.25rem",
                              ml: "0.25rem",
                            }}
                            color="error"
                          />
                        </span>
                        {fileName}
                      </div>
                      <ErrorMessage name="file" component={ShowError} />
                    </>
                  )}
                </div>

                <div className="rotate-180 pe-4">
                  {formik.isSubmitting ? (
                    <CircularProgress size={25} />
                  ) : (
                    <IconButton
                      disabled={
                        formik.values.file === "" &&
                        formik.values.context.trim() === ""
                      }
                      onClick={() => formik.submitForm()}
                    >
                      <SendIcon
                        color={
                          formik.values.file === "" &&
                          formik.values.context.trim() === ""
                            ? "disabled"
                            : "primary"
                        }
                      />
                    </IconButton>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </ModalContainer>
  );
};

export default Context;
